import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, effect, signal, ElementRef, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ICrossAppMessageData, SupportedCrossAppEventType, ICrossAppMessage, IFileHydrated, IAreaElement } from '@newroom-connect/library/interfaces';
import { FileHelper } from '@newroom-connect/library/helpers';
import { AreaElementService, CrossAppMessageService, LanguageService, TrackingEventService } from '@newroom-connect/library/services';
import { BoundingBox } from '@newroom-connect/library/types';
import { ClickOutsideDirective } from '@newroom-connect/library/directives';
import { IsNonEmptyArrayPipe } from '@newroom-connect/library/pipes';
import { TrackingEventType } from '@newroom-connect/library/enums';

import { AreaElementComponent } from '../area-element/area-element.component';
import { SceneOverlayComponent } from '../../scene-overlay/scene-overlay.component';

import { AreaBackground2DComponent } from './area-background-2d/area-background-2d.component';
import { AreaBackground360DegreeComponent } from './area-background-360-degree/area-background-360-degree.component';
import { AreaBackgroundEmptyComponent } from './area-background-empty/area-background-empty.component';
import { HtmlBackgroundComponent } from './html-background/html-background.component';

export interface ISelectedAreaElementConfig {
  id: string;
  isResizableStatus: boolean;
  keepAspectRatio: boolean;
}

@Component({
  selector: 'nrc-area-background',
  standalone: true,
  imports: [
    CommonModule,
    AreaBackground2DComponent,
    AreaBackground360DegreeComponent,
    AreaBackgroundEmptyComponent,
    HtmlBackgroundComponent,
    AreaElementComponent,
    IsNonEmptyArrayPipe,
    SceneOverlayComponent,
    ClickOutsideDirective
  ],
  templateUrl: './area-background.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AreaBackgroundComponent implements OnChanges {
  @ViewChild('areaBackgroundContainer') public areaBackgroundContainerRef!: ElementRef<HTMLDivElement>;

  @Input({ required: true }) public areaElements: IAreaElement[] = [];
  @Input({ required: true }) public apiBaseUrl = '';
  @Input() public backgroundFile: IFileHydrated | null = null;
  @Input() public isEditMode = true;
  @Input() public selectedAreaElementConfig?: ISelectedAreaElementConfig;

  @Output() public changeBackgroundEvent = new EventEmitter<void>();
  @Output() public backgroundActionEvent = new EventEmitter<ICrossAppMessageData>();
  @Output() public loadComplete = new EventEmitter<void>();

  @Output() public areaElementClickedEvent = new EventEmitter<IAreaElement>();
  @Output() public areaElementDblClickedEvent = new EventEmitter<IAreaElement>();
  @Output() public areaElementChangedEvent = new EventEmitter<IAreaElement>();

  public isBackgroundFileCubemapSig = signal<boolean>(false);
  public isBackgroundFileHTMLSig = signal<boolean>(false);
  public isLoading = signal<boolean>(false);

  public isBackgroundActionEventActive = false;

  public currentLanguageCodeSig = computed<string>(() => {
    const languageCode = this.languageService.watchSelectedLanguageCode()();

    return languageCode ?? '';
  });

  /**
   * @constructor
   *
   * @param crossAppMessageService
   * @param languageService
   * @param areaElementService
   * @param trackingEventService
   */
  constructor(
    private readonly crossAppMessageService: CrossAppMessageService,
    private readonly languageService: LanguageService,
    public readonly areaElementService: AreaElementService,
    private readonly trackingEventService: TrackingEventService
  ) {
    effect(() => {
      const isBackgroundFileHtml = this.isBackgroundFileHTMLSig();

      // Only register the background html action events if not in edit mode and the background is a html file.
      if (isBackgroundFileHtml && !this.isBackgroundActionEventActive) {
        this.isBackgroundActionEventActive = true;
        this.crossAppMessageService.registerHandler(SupportedCrossAppEventType.HTML_BACKGROUND, this.handleBackgroundActionEvent.bind(this));
        this.crossAppMessageService.registerHandler(SupportedCrossAppEventType.HTML_BACKGROUND, this.handleBackgroundTrackingEvent.bind(this));
      }
    });
  }

  /**
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['backgroundFile']) {
      // Set loading to true when background file changes
      this.isLoading.set(true);

      const isCubemap = changes['backgroundFile'].currentValue ? FileHelper.isFileCubemap(changes['backgroundFile'].currentValue) : false;
      const isHTML = changes['backgroundFile'].currentValue ? FileHelper.isFileHTML(changes['backgroundFile'].currentValue) : false;

      this.isBackgroundFileCubemapSig.set(isCubemap);
      this.isBackgroundFileHTMLSig.set(isHTML);

      // When background file changes (not initial load), mark all area elements with htmlElementId for deletion
      if (!changes['backgroundFile'].firstChange && this.areaElements) {
        const currentFile = changes['backgroundFile'].currentValue;
        const previousFile = changes['backgroundFile'].previousValue;

        // Only process if it's actually a different file
        if (!currentFile || !previousFile || currentFile.id === previousFile.id) {
          return;
        }

        for (const areaElement of this.areaElements) {
          if (areaElement.metadata?.htmlElementId && !areaElement.markedForCreation) {
            this.areaElementChangedEvent.emit({ ...areaElement, markedForDeletion: true });
          }
        }
      }
    }
  }

  /**
   * Handle background load completion.
   */
  public onBackgroundLoadComplete(): void {
    this.isLoading.set(false);
    this.loadComplete.emit();
  }

  /**
   * Handles the BackgroundEvent if the event type is not HTML_BACKGROUND.
   * @param event
   */
  private handleBackgroundActionEvent(event: ICrossAppMessage): void {
    if (event.type !== SupportedCrossAppEventType.HTML_BACKGROUND) {
      return;
    }

    this.backgroundActionEvent.emit(event.data);
  }

  /**
   * Creates a tracking Event if event type is HTML_BACKGROUND.
   * @param event The crossAppEvent that will be processed.
   */
  private handleBackgroundTrackingEvent(event: ICrossAppMessage):void {
    if (event.type !== SupportedCrossAppEventType.HTML_BACKGROUND) {
      return;
    }

    this.trackingEventService.createTrackingEvent({ eventType: TrackingEventType.CROSS_APP_TRACKING_EVENT, metadata: event.data?.metadata });
  }

  /**
   *
   * @param payload
   * @param payload.id
   * @param payload.boundingBox
   */
  public handleAreaElementBoundingBoxChange(payload: { id: string, boundingBox: BoundingBox }): void {
    const areaElement = this.areaElements?.find(element => element.id === payload.id);

    if (!areaElement) {
      return;
    }

    this.areaElementChangedEvent.emit({ ...areaElement, boundingBox: payload.boundingBox });
  }

  /**
   *
   * @param areaElement
   */
  public handleAreaElementClickEvent(areaElement: IAreaElement): void {
    this.areaElementClickedEvent.emit(areaElement);
  }
}
